import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useNgoPortalsQuery } from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import { Suspense } from 'react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { MailIcon } from 'lucide-react';
import { ExportIcon } from '@worldcoo/ui/custom/export-icon';
import { Button } from '@worldcoo/ui/components/button';
import { MainSectionHeader, MainSectionContent } from './-components/main-section';
import { DonatingIllustration } from '@worldcoo/ui/custom/donating-illustration';
import { Image } from '../-components/image';
import { buildPortalUrl } from '@/lib/shared';
import { stripHtml } from '@worldcoo/shared/utils';

export const Route = createFileRoute('/ngos/portals/')({
	loader: ({ context }) => {
		context.queryClient.ensureQueryData(trpc.ngo.getPortals.queryOptions());
	},
	component: RouteComponent,
});

function RouteComponent() {
	return (
		<Suspense fallback={<PortalsListSkeleton />}>
			<PortalsList />
		</Suspense>
	);
}

function PortalsList() {
	const { t } = useLingui();
	const data = useNgoPortalsQuery();
	const portals = data.portals;

	return (
		<>
			{/* Header */}
			<MainSectionHeader
				title={<h1 className="text-foreground text-2xl font-medium tracking-[-0.2px]">{t`Portals`}</h1>}
			/>

			<MainSectionContent className="p-4 md:p-6">
				{portals.length === 0 ? (
					<EmptyState />
				) : (
					<div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-2">
						{portals.map((portal) => (
							<PortalCard key={portal.id} portal={portal} />
						))}
						<NeedNewPortalCard />
					</div>
				)}
			</MainSectionContent>
		</>
	);
}

type PortalSummary = ReturnType<typeof useNgoPortalsQuery>['portals'][number];

function PortalCard(props: { portal: PortalSummary }) {
	const { t } = useLingui();
	// Unconfigured portals (no `updatedAt`) deep-link straight into the setup wizard;
	// configured ones go to the detail page. Image + button share the target.
	const isConfigured = !!props.portal.updatedAt;
	const cardLinkTo = isConfigured ? '/ngos/portals/$portalId' : '/ngos/portals/$portalId/setup';
	const cardLinkLabel = isConfigured ? t`View details` : t`Configure`;

	return (
		<div className="flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0px_8px_24px_0px_rgba(77,33,0,0.05)]">
			{/* Cover image */}
			<Link
				to={cardLinkTo}
				params={{ portalId: props.portal.id }}
				className="block h-[179px] w-full overflow-hidden"
				style={{ backgroundColor: props.portal.themeBaseColor || '#f28e41' }}
			>
				{props.portal.media?.location && (
					<Image
						src={props.portal.media.location}
						alt={props.portal.title ?? ''}
						height={358}
						aspectRatio={16 / 9}
						objectFit="cover"
						className="h-full w-full object-cover"
					/>
				)}
			</Link>

			{/* Content */}
			<div className="flex flex-1 flex-col gap-2 p-4 md:p-6">
				<h3 className="text-foreground text-xl font-medium tracking-[-0.2px] md:text-2xl">
					{props.portal.title ?? props.portal.id}
				</h3>
				{props.portal.presentation && (
					<p className="text-muted-foreground line-clamp-3 text-sm md:text-base">
						{stripHtml(props.portal.presentation)}
					</p>
				)}

				{/* Actions */}
				<div className="mt-2 flex flex-wrap items-center justify-between gap-2">
					<Button variant="default" size="xl" asChild>
						<Link to={cardLinkTo} params={{ portalId: props.portal.id }}>
							{cardLinkLabel}
						</Link>
					</Button>
					<Button variant="outline-warm" size="xl" className="gap-2" asChild>
						<a href={buildPortalUrl(props.portal.id)} target="_blank" rel="noopener noreferrer" title={t`Open`}>
							<ExportIcon className="size-5" />
							<span>{t`Open`}</span>
						</a>
					</Button>
				</div>
			</div>
		</div>
	);
}

function EmptyState() {
	const { t } = useLingui();

	return (
		<div className="flex flex-col items-center justify-center py-20">
			<DonatingIllustration className="mb-8 h-48 w-auto text-[#f28e41]" />
			<h2 className="text-foreground mb-2 text-xl font-semibold">{t`Welcome to your portals`}</h2>
			<p className="text-muted-foreground mb-8 max-w-md text-center text-sm">
				{t`You don't have any portals yet. Contact the Worldcoo team to set up your first donation portal, or discover how portals work.`}
			</p>
			<div className="flex gap-3">
				<Button variant="outline-dark" size="lg" asChild>
					<a href="mailto:contact@worldcoo.com">
						<MailIcon className="size-4" />
						{t`Contact`}
					</a>
				</Button>
				<Button variant="default" size="lg" asChild>
					<Link to="/ngos/faq" hash="faq-portals">{t`Learn more about portals`}</Link>
				</Button>
			</div>
		</div>
	);
}

function NeedNewPortalCard() {
	const { t } = useLingui();

	return (
		<div className="relative flex flex-col justify-end overflow-hidden rounded-2xl bg-[#fff7ed]">
			<img
				src="/illustration-my-portal.png"
				alt=""
				className="pointer-events-none absolute -top-12 right-[-20px] h-[320px] w-auto rotate-[8deg] opacity-80"
			/>
			<div className="relative z-10 mt-auto flex flex-col gap-4 px-8 py-8">
				<h3 className="text-foreground text-lg font-medium">{t`Need a new portal?`}</h3>
				<p className="text-muted-foreground max-w-xs text-base">
					{t`If you'd like to create an additional portal, please contact the Worldcoo team.`}
				</p>
				<div>
					<Button variant="outline-dark" size="lg" asChild>
						<a href="mailto:contact@worldcoo.com">{t`Contact`}</a>
					</Button>
				</div>
			</div>
		</div>
	);
}

function PortalsListSkeleton() {
	return (
		<>
			<MainSectionHeader title={<Skeleton className="h-8 w-32" />} />
			<MainSectionContent className="p-6">
				<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
					{Array.from({ length: 2 }).map((_, i) => (
						<div
							// oxlint-disable-next-line react/no-array-index-key
							key={`skeleton-${i}`}
							className="flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0px_8px_24px_0px_rgba(77,33,0,0.05)]"
						>
							<Skeleton className="h-[179px] w-full rounded-none" />
							<div className="flex flex-col gap-2 p-6">
								<Skeleton className="h-7 w-3/4" />
								<Skeleton className="h-4 w-full" />
								<Skeleton className="h-4 w-full" />
								<Skeleton className="h-4 w-2/3" />
								<div className="mt-2 flex items-center justify-between">
									<Skeleton className="h-12 w-32 rounded-xl" />
									<div className="flex gap-2">
										<Skeleton className="size-12 rounded-xl" />
										<Skeleton className="size-12 rounded-xl" />
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			</MainSectionContent>
		</>
	);
}
